/**
 * GlobalView — HUD Overlay (§11.6)
 *
 * Translucent coordinate display in the bottom-left of the viewport.
 * Shows cursor position (LAT/LON), eye altitude, and view mode.
 */

import { useGlobeStore } from '../../stores/globeStore';
import styles from './HudOverlay.module.css';

/** Format decimal degrees to DMS-like string */
function formatCoord(value: number, isLat: boolean): string {
  const dir = isLat
    ? value >= 0 ? 'N' : 'S'
    : value >= 0 ? 'E' : 'W';
  const abs = Math.abs(value);
  return `${abs.toFixed(5)}° ${dir}`;
}

/** Format altitude for display */
function formatAlt(meters: number): string {
  if (meters >= 1_000_000) return `${(meters / 1_000_000).toFixed(1)} Mm`;
  if (meters >= 1_000) return `${(meters / 1_000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

const VIEW_MODE_LABELS = {
  globe: 'GLOBE VIEW',
  region: 'REGION VIEW',
  city: 'CITY TACTICAL',
} as const;

export default function HudOverlay() {
  const cursorPosition = useGlobeStore((s) => s.cursorPosition);
  const cameraPosition = useGlobeStore((s) => s.cameraPosition);
  const viewMode = useGlobeStore((s) => s.viewMode);
  const ready = useGlobeStore((s) => s.ready);

  if (!ready) return null;

  return (
    <div className={styles.overlay} id="hud-overlay">
      {/* Cursor Position */}
      {cursorPosition ? (
        <>
          <div className={styles.row}>
            <span className={styles.label}>LAT</span>
            <span className={styles.value}>
              {formatCoord(cursorPosition.lat, true)}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>LON</span>
            <span className={styles.value}>
              {formatCoord(cursorPosition.lon, false)}
            </span>
          </div>
        </>
      ) : (
        <div className={styles.row}>
          <span className={styles.label}>CURSOR</span>
          <span className={styles.value} style={{ opacity: 0.3 }}>—</span>
        </div>
      )}

      <hr className={styles.separator} />

      {/* Eye Altitude */}
      <div className={styles.row}>
        <span className={styles.label}>EYE ALT</span>
        <span className={styles.value}>
          {formatAlt(cameraPosition.alt)}
        </span>
      </div>

      {/* View Mode */}
      <div className={styles.viewMode}>
        {VIEW_MODE_LABELS[viewMode]}
      </div>
    </div>
  );
}
